import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import type { CrmMessageStatementItem } from "../types";

export interface CrmMessageStatementDialogProps {
  open: boolean;
  items: CrmMessageStatementItem[];
  onClose: () => void;
}

const CrmMessageStatementDialog = memo<CrmMessageStatementDialogProps>(
  ({ open, items, onClose }) => {
    const rendered = useMemo(() => {
      if (items.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            Nenhum envio simulado encontrado para exibir no extrato.
          </Typography>
        );
      }

      return (
        <List dense disablePadding>
          {items.map((item) => {
            const statusLabel =
              item.status === "sent"
                ? "Enviado"
                : item.status === "scheduled"
                  ? "Agendado"
                  : "Falha";

            const statusColor =
              item.status === "sent"
                ? "success.main"
                : item.status === "scheduled"
                  ? "warning.main"
                  : "error.main";

            return (
              <ListItem
                key={item.id}
                sx={{
                  px: 0,
                  py: 0.75,
                  borderBottom: "1px dashed",
                  borderColor: "divider",
                  "&:last-of-type": {
                    borderBottom: "none",
                  },
                }}
              >
                <ListItemText
                  primary={<Typography variant="body1">{item.description}</Typography>}
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {item.channelLabel} • {item.quantity.toLocaleString("pt-BR")}{" "}
                      mensagens •{" "}
                      <Box component="span" sx={{ color: statusColor, fontWeight: 700 }}>
                        {statusLabel}
                      </Box>
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      );
    }, [items]);

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Extrato de mensagens
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Esta visão é um mock: no futuro, será carregada via API com filtros por
            período, status e automação.
          </Typography>
          {rendered}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CrmMessageStatementDialog.displayName = "CrmMessageStatementDialog";

export default CrmMessageStatementDialog;

